import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import ButtonLoader from './ButtonLoader';
import { update_user_profile, getAuthMetadataService } from '../services';

const INDUSTRY_GROUPS = [
    'Financial_Services',
    'Healthcare_Life_Sciences',
    'Technology',
    'Retail_Commerce',
    'Manufacturing_Industrial',
    'Energy_Utilities',
    'Government_Public_Sector',
    'Professional_Services',
    'Telecom_Infrastructure',
    'Logistics_Transportation',
    'Education',
    'Hospitality_Media'
];

const REGULATORY_PROFILES = {
    India: [
        'RBI_Banks_Cybersecurity',
        'RBI_NBFC_Cybersecurity',
        'SEBI_CSCRF',
        'IRDAI_Cybersecurity',
        'DPDP_Act',
        'CERT-In_Directions',
        'MeitY_EMPowerment',
        'ISO_27001',
        'ISO_27701',
        'SOC_2',
        'PCI_DSS',
        'NCA_ECC'
    ],
    USA: [
        'FedRAMP',
        'NIST_800-53',
        'SEC_Cyber_Rules',
        'HIPAA',
        'GLBA',
        'PCI_DSS',
        'CISA_Critical_Infrastructure',
        'State_Privacy (CCPA/CPRA)',
        'SOC_2',
        'NIST_CSF',
        'ISO_27001'
    ],
    US: [ // Alias for USA
        'FedRAMP',
        'NIST_800-53',
        'SEC_Cyber_Rules',
        'HIPAA',
        'GLBA',
        'PCI_DSS',
        'CISA_Critical_Infrastructure',
        'State_Privacy (CCPA/CPRA)',
        'SOC_2',
        'NIST_CSF',
        'ISO_27001'
    ],
    UK: [
        'DORA',
        'FCA_Security_Expectations',
        'NIS2',
        'UK_GDPR',
        'Cyber_Essentials_Plus',
        'Cyber_Essentials',
        'ICO_Guidance',
        'SOC_2',
        'ISO_27001'
    ],
    EU: [
        'DORA',
        'NIS2',
        'GDPR',
        'SOC_2',
        'ISO_27001'
    ],
    'EU/UK': [ // Combined EU/UK profiles
        'DORA',
        'FCA_Security_Expectations',
        'NIS2',
        'GDPR',
        'UK_GDPR',
        'Cyber_Essentials_Plus',
        'Cyber_Essentials',
        'ICO_Guidance',
        'SOC_2',
        'ISO_27001'
    ],
    GCC: [
        'NCA_ECC',
        'SAMA_CSF',
        'ADHICS',
        'NESA',
        'QCB_CSF',
        'Dubai_ISR',
        'KSA_PDPL',
        'UAE_PDPL',
        'SOC_2',
        'ISO_27001'
    ]
};

// Helper function to format display text (replace underscores with spaces)
const formatDisplayText = (text) => {
    return text.replace(/_/g, ' ');
};

function ProfileCompletionModal({ show, onHide, onSuccess, existingProfile }) {
    const [formData, setFormData] = useState({
        industry_group: existingProfile?.industry_group || '',
        region: existingProfile?.region || '',
        primary_regulatory_profile: existingProfile?.primary_regulatory_profile || '',
        customer_size: parseInt(existingProfile?.customer_size) || ''
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [resMsg, setResMsg] = useState('');
    const [errResMsg, setErrResMsg] = useState('');
    const [regions, setRegions] = useState([]);
    const [loadingMetadata, setLoadingMetadata] = useState(false);

    // Fetch metadata (regions) on mount
    useEffect(() => {
        const fetchMetadata = async () => {
            setLoadingMetadata(true);
            try {
                const response = await getAuthMetadataService();
                const regionsData = response?.statusCode?.response_data?.data?.regions ||
                    response?.data?.response_data?.data?.regions ||
                    [];
                setRegions(regionsData);
            } catch (error) {
                console.error('Error fetching metadata:', error);
            } finally {
                setLoadingMetadata(false);
            }
        };

        if (show) {
            fetchMetadata();
        }
    }, [show]);

    // Clear form data and messages when modal closes
    useEffect(() => {
        if (!show) {
            setFormData({
                industry_group: '',
                region: '',
                primary_regulatory_profile: '',
                customer_size: ''
            });
            setErrors({});
            setResMsg('');
            setErrResMsg('');
        }
    }, [show]);

    // Get region name from ID to lookup regulatory profiles
    const getSelectedRegionName = () => {
        const selectedRegion = regions.find(r => r.id === parseInt(formData.region));
        return selectedRegion?.name || '';
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = {
                ...prev,
                [name]: value
            };
            // Clear regulatory profile when region changes
            if (name === 'region') {
                newData.primary_regulatory_profile = '';
            }
            return newData;
        });
        // Clear error for this field
        setErrors(prev => ({
            ...prev,
            [name]: ''
        }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.industry_group) {
            newErrors.industry_group = 'Industry group is required';
        }
        if (!formData.region) {
            newErrors.region = 'Region is required';
        }
        if (!formData.primary_regulatory_profile) {
            newErrors.primary_regulatory_profile = 'Regulatory profile is required';
        }
        if (!formData.customer_size) {
            newErrors.customer_size = 'Number of employees is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setIsLoading(true);
        setResMsg('');
        setErrResMsg('');

        try {
            const params = {
                industry_group: formData.industry_group,
                region: parseInt(formData.region),
                primary_regulatory_profile: formData.primary_regulatory_profile,
                customer_size: parseInt(formData.customer_size)
            };

            const response = await update_user_profile(params);

            const successMessage =
                response?.statusCode?.response_data?.message ||
                response?.statusCode?.message ||
                response?.data?.message;
            const errorMessage =
                response?.errors?.message ||
                response?.error?.message ||
                response?.statusCode?.error?.message;

            if (successMessage && !errorMessage) {
                setResMsg(successMessage || 'Profile updated successfully!');
                // Update localStorage with new profile data (inside organization key)
                const existingProfileData = JSON.parse(localStorage.getItem('user_profile') || '{}');
                const updatedProfile = {
                    ...existingProfileData,
                    organization: {
                        ...(existingProfileData.organization || {}),
                        ...params
                    }
                };
                localStorage.setItem('user_profile', JSON.stringify(updatedProfile));

                setTimeout(() => {
                    onSuccess && onSuccess();
                    onHide();
                }, 1000);
            } else if (errorMessage) {
                setErrResMsg(errorMessage || 'Failed to update profile. Please try again.');
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            setErrResMsg('Failed to update profile. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            backdrop="static"
            keyboard={false}
            className="cstm_modal"
        >
            <Modal.Header closeButton={false}>
                <Modal.Title>Complete Your Profile</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <p className="text-white mb-4">
                    Please complete your profile information to continue.
                </p>

                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label className="text-white fw-semibold">
                            Industry Group <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                            name="industry_group"
                            value={formData.industry_group}
                            onChange={handleChange}
                            className="form-control"
                        >
                            <option value="">Select Industry Group</option>
                            {INDUSTRY_GROUPS.map((industry, index) => (
                                <option key={index} value={industry}>
                                    {formatDisplayText(industry)}
                                </option>
                            ))}
                        </Form.Select>
                        {errors.industry_group && (
                            <small className="text-danger">{errors.industry_group}</small>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="text-white fw-semibold">
                            Region <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                            name="region"
                            value={formData.region}
                            onChange={handleChange}
                            className="form-control"
                            disabled={loadingMetadata}
                        >
                            <option value="">{loadingMetadata ? 'Loading...' : 'Select Region'}</option>
                            {regions.map((region) => (
                                <option key={region.id} value={region.id}>
                                    {region.name}
                                </option>
                            ))}
                        </Form.Select>
                        {errors.region && (
                            <small className="text-danger">{errors.region}</small>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="text-white fw-semibold">
                            Primary Regulatory Profile <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                            name="primary_regulatory_profile"
                            value={formData.primary_regulatory_profile}
                            onChange={handleChange}
                            className="form-control"
                            disabled={!formData.region}
                        >
                            <option value="">{formData.region ? 'Select Regulatory Profile' : 'Select Region First'}</option>
                            {formData.region && REGULATORY_PROFILES[getSelectedRegionName()]?.map((profile, index) => (
                                <option key={index} value={profile}>
                                    {formatDisplayText(profile)}
                                </option>
                            ))}
                        </Form.Select>
                        {errors.primary_regulatory_profile && (
                            <small className="text-danger">{errors.primary_regulatory_profile}</small>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="text-white fw-semibold">
                            Number of employees <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="number"
                            name="customer_size"
                            value={formData.customer_size}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Enter number of customers"
                            min="1"
                        />
                        {errors.customer_size && (
                            <small className="text-danger">{errors.customer_size}</small>
                        )}
                    </Form.Group>

                    {resMsg && <p className="text-success mb-3">{resMsg}</p>}
                    {errResMsg && <p className="text-danger mb-3">{errResMsg}</p>}

                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={isLoading}
                        >
                            {isLoading ? <ButtonLoader /> : 'Save & Continue'}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}

export default ProfileCompletionModal;
